import React, { useState, useEffect, useMemo } from "react";
import {
  notificationMockData,
  NotificationItemType,
  markAsReadApi,
  deleteNotificationApi,
  markAllAsReadApi,
  fetchNotifications,
  fetchUnreadCount,
} from "./notificationMockData";
import { notificationIconMap } from "./notificationIcons";
import { ArrowLeft, Check, Trash2 } from "lucide-react";
import PaginationControlsView from "components/PaginationViewtables";
import { Table } from "@tanstack/react-table";

import { getAuthCookie } from "../../utils/authCookies";

const NotificationListPage: React.FC = () => {
  const [items, setItems] = useState<NotificationItemType[]>([]);
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0); // 0-indexed for table compatibility
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // Calculate page count
  const pageCount = useMemo(() => {
    return Math.ceil(total / limit) || 1;
  }, [total, limit]);

  // Create a mock table object for PaginationControlsView
  const mockTable = useMemo(() => {
    return {
      getPageCount: () => pageCount,
      getState: () => ({
        pagination: {
          pageIndex: page,
          pageSize: limit,
        },
      }),
      setPageSize: (newSize: number) => {
        setLimit(newSize);
        setPage(0); // Reset to first page when changing page size
      },
      setPageIndex: (newIndex: number) => {
        setPage(newIndex);
      },
      previousPage: () => {
        if (page > 0) {
          setPage(page - 1);
        }
      },
      nextPage: () => {
        if (page < pageCount - 1) {
          setPage(page + 1);
        }
      },
      getCanPreviousPage: () => page > 0,
      getCanNextPage: () => page < pageCount - 1,
    } as Table<any>;
  }, [page, limit, pageCount]);

  // Fetch notifications with pagination
  const loadNotifications = async (currentPage: number, currentLimit: number, seenFilter: "seen" | "unseen" | "all" = "all") => {
    const userId = getAuthCookie("userId");
    const token = getAuthCookie("sessionToken");

    if (!userId || !token) {
      setError("Please log in to view notifications");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await fetchNotifications(userId, token, {
        page: currentPage + 1, // API uses 1-indexed pages
        limit: currentLimit,
        seen: seenFilter,
        forceRefresh: true,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setError("Failed to load notifications");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on mount and when pagination changes
  useEffect(() => {
    const seenFilter = activeTab === "unread" ? "unseen" : "all";
    loadNotifications(page, limit, seenFilter);
  }, [page, limit, activeTab]);

  // Get unread count from API
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const loadUnreadCount = async () => {
      const userId = getAuthCookie("userId");
      const token = getAuthCookie("sessionToken");
      
      if (!userId || !token) return;
      
      try {
        const count = await fetchUnreadCount(userId, token);
        setUnreadCount(count);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    loadUnreadCount();
    // Refresh unread count periodically
    const interval = setInterval(loadUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Bump unread count immediately when a realtime notification arrives
  useEffect(() => {
    const handleRealtimeNotification = () => {
      setUnreadCount((prev) => prev + 1);
    };

    window.addEventListener(
      "realesy:notification",
      handleRealtimeNotification
    );
    return () => {
      window.removeEventListener(
        "realesy:notification",
        handleRealtimeNotification
      );
    };
  }, []);

  const grouped = {
    today: items.filter((i) => i.dateGroup === "today"),
    yesterday: items.filter((i) => i.dateGroup === "yesterday"),
    this_week: items.filter((i) => i.dateGroup === "this_week"),
  };

  /* ---------------------------
      ⭐ ACTIONS
  --------------------------- */

  // Refresh notifications from API
  const refreshNotifications = async () => {
    const seenFilter = activeTab === "unread" ? "unseen" : "all";
    await loadNotifications(page, limit, seenFilter);
  };

  const handleMarkAsRead = async (id: string) => {
    if (!id || id === "undefined" || id.trim() === "") {
      console.error("Invalid notification ID:", id);
      return;
    }

    try {
      await markAsReadApi(id);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isUnread: false } : n))
      );
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotificationApi(id);
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadIds = items.filter((n) => n.isUnread).map((n) => n.id);
      await markAllAsReadApi(unreadIds);
      // Refresh to get updated data from API
      await refreshNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  /* ---------------------------
      ⭐ UI
  --------------------------- */

  // Show loading state
  if (loading) {
    return (
      <div className="container" style={{ maxWidth: "760px", padding: "20px" }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && items.length === 0) {
    return (
      <div className="container" style={{ maxWidth: "760px", padding: "20px" }}>
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: "760px", padding: "20px" }}>
      {/* HEADER */}
      <div className="mb-4">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div className="d-flex align-items-start">
            <button
              className="btn btn-link p-0 me-2 mt-1"
              style={{ 
                border: "none", 
                background: "transparent",
                cursor: "pointer",
                padding: "2px",
              }}
              onClick={() => window.history.back()}
            >
              <ArrowLeft size={20} color="#6b7280" />
            </button>
            <div>
              <h3 className="fw-bold m-0 mb-1" style={{ fontSize: "20px", color: "#111827" }}>
                Notifications
              </h3>
              <div style={{ color: "#6b7280", fontSize: "13px" }}>
                {unreadCount} unread
              </div>
            </div>
          </div>
          <button
            className="btn btn-link p-0 d-flex align-items-center gap-1 mt-1"
            onClick={handleMarkAllAsRead}
            style={{
              border: "none",
              background: "transparent",
              color: "#155dfc",
              textDecoration: "none",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            <Check size={16} color="#155dfc" />
            <span>Mark all as read</span>
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="d-flex gap-2 mb-4">
        <button
          className={`btn px-4 py-1 ${
            activeTab === "all"
              ? "btn-primary"
              : "btn-light border"
          }`}
          onClick={() => setActiveTab("all")}
          style={{
            borderRadius: "20px",
            fontSize: "14px",
            fontWeight: activeTab === "all" ? "500" : "400",
          }}
        >
          All
        </button>

        <button
          className={`btn px-4 py-1 ${
            activeTab === "unread"
              ? "btn-primary"
              : "btn-light border"
          }`}
          onClick={() => setActiveTab("unread")}
          style={{
            borderRadius: "20px",
            fontSize: "14px",
            fontWeight: activeTab === "unread" ? "500" : "400",
          }}
        >
          Unread ({unreadCount})
        </button>
      </div>

      {/* EMPTY STATE */}
      {items.length === 0 && !loading && (
        <div className="text-center py-5">
          <p className="text-muted">No notifications found</p>
        </div>
      )}

      {/* SECTIONS */}
      {Object.entries(grouped).map(([label, list]) => {
        if (list.length === 0) return null;

        const title =
          label === "today"
            ? "Today"
            : label === "yesterday"
            ? "Yesterday"
            : "This Week";

        return (
          <div className="mb-4" key={label}>
            <h6
              className="fw-semibold mb-3"
              style={{ color: "#6b7280", fontSize: "14px" }}
            >
              {title}
            </h6>

            {/* GROUPED CARD CONTAINER */}
            <div
              className="rounded"
              style={{
                background: "#ffffff",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
              }}
            >
              {list.map((n, index) => {
                const iconConfig = notificationIconMap[n.type];
                const isLast = index === list.length - 1;
                
                return (
                  <div key={n.id}>
                    <div
                      className="d-flex align-items-start p-3"
                      style={{
                        background: n.isUnread ? "#eef4ff" : "#ffffff",
                        transition: "0.2s",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = n.isUnread ? "#e0ebff" : "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = n.isUnread ? "#eef4ff" : "#ffffff";
                      }}
                    >
                      {/* LEFT ICON */}
                      <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: iconConfig.color,
                          flexShrink: 0,
                        }}
                      >
                        {iconConfig.icon}
                      </div>

                      {/* CONTENT */}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div
                          className="d-flex align-items-center gap-2 mb-1"
                          style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}
                        >
                          <span>{n.title}</span>
                          {n.isUnread && (
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#155dfc",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </div>
                        <div
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            color: "#4b5563",
                            lineHeight: "18px",
                          }}
                        >
                          {n.description}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            marginTop: "6px",
                          }}
                        >
                          {n.time}
                        </div>
                      </div>

                      {/* RIGHT ICONS */}
                      <div
                        className="d-flex align-items-center gap-2 ms-2"
                        style={{ flexShrink: 0 }}
                      >
                        {/* CHECKMARK - Only show if unread (to mark as read) */}
                        {n.isUnread && (
                          <button
                            className="btn btn-link p-0"
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: "4px",
                              color: "#6b7280",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(n.id);
                            }}
                            title="Mark as read"
                          >
                            <Check size={18} strokeWidth={2} />
                          </button>
                        )}
                        
                        {/* DELETE */}
                        <button
                          className="btn btn-link p-0"
                          style={{
                            border: "none",
                            background: "transparent",
                            padding: "4px",
                            color: "#6b7280",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(n.id);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={18} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                    
                    {/* DIVIDER - Show between items, not after last */}
                    {!isLast && (
                      <div
                        style={{
                          height: "1px",
                          background: "#e5e7eb",
                          margin: "0 16px",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* PAGINATION */}
      {items.length > 0 && (
        <div className="mt-4">
          <PaginationControlsView table={mockTable} />
        </div>
      )}
    </div>
  );
};

export default NotificationListPage;
